import React, { useEffect, useState } from 'react';
import {
  formatDate,
  useNotification,
  getButtonSpinner,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { intialPageSize } from 'constants';
import Typography from '@mui/material/Typography';
import { GridWrapper } from 'components/Ui/styled';
import { useDispatch, useSelector } from 'react-redux';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import { getStudentCourseData } from 'layouts/students/service/studentsApi';
import { renderCellExpand } from 'components/Common/GridCell/GridCellExpand';
import { setStudentProfileLiveCourseData } from 'layouts/studentManagement/store/studentProfileLiveCourseSlice/studentProfileLiveCourseSlice';

const LiveCourseDataTab = () => {
  const dispatch = useDispatch();
  const { handleErrorResponse, NotificationPopup } = useNotification();

  const studentDetails = useSelector((state) => state?.studentDetails) || {};
  const { profile } = studentDetails || {};
  const { resourceId } = profile || {};

  const liveCourseData = useSelector(
    (state) => state?.studentLiveCourseData?.byStudentId?.[resourceId],
  );

  const [isLoading, setIsLoading] = useState(false);

  const [liveCoursesList, setLiveCoursesList] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
  });

  const getStudentLiveCourseData = async (forceRefresh = false) => {
    if (!resourceId) return;

    if (liveCourseData?.loaded && !forceRefresh) return;

    try {
      setLiveCoursesList({ ...liveCoursesList, isLoading: true });
      const response = await getStudentCourseData(resourceId);
      dispatch(
        setStudentProfileLiveCourseData({
          studentId: resourceId,
          data: response,
        }),
      );
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setLiveCoursesList({ ...liveCoursesList, isLoading: false });
    }
  };

  useEffect(() => {
    getStudentLiveCourseData();
  }, [resourceId]);

  useEffect(() => {
    if (!liveCourseData?.courseData) return;

    setLiveCoursesList((prev) => ({
      ...prev,
      rows: liveCourseData?.courseData?.map((item, index) => ({
        ...item,
        id: index,
        metadataCompleted: item.providerMetadata?.completed,
        lastOnlineActivity: formatDate(item.lastOnlineActivity),
        metadataAssignedDate: formatDate(item.providerMetadata?.assigned_date),
      })),
    }));
  }, [liveCourseData]);

  const pageSizeChange = pageSizeChangeHandler(setLiveCoursesList);

  const createColumn = (field, headerName, type = '', flex = 1) => {
    const column = { field, headerName, flex, type };
    if (type !== 'boolean') column.renderCell = renderCellExpand;
    return column;
  };

  const columns = [
    createColumn('courseName', 'Course Name'),
    createColumn('provider', 'Provider'),
    createColumn('actualGrade', 'Grade'),
    createColumn('providerCourseId', 'Course ID'),
    createColumn('metadataCompleted', 'Metadata Completed', 'boolean'),
    createColumn('lastOnlineActivity', 'Last Online Activity'),
    createColumn('metadataAssignedDate', 'Metadata Assigned Date'),
  ];

  const providersList = Array.isArray(liveCourseData?.providersQueried)
    ? liveCourseData.providersQueried.join(', ')
    : '-';

  const refetchLiveCourse = async () => {
    setIsLoading(true);
    await getStudentLiveCourseData(true);
    setIsLoading(false);
  };

  return (
    <>
      <MDBox pt={2} />
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6">
            Providers Queried : {providersList}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4} justifyItems="end">
          {getButtonSpinner(
            isLoading,
            refetchLiveCourse,
            'Re-fetch Live Course',
          )}
        </Grid>
      </Grid>

      <GridWrapper container spacing={2}>
        <DeltaDataGrid
          listData={liveCoursesList}
          columns={columns}
          setListData={pageSizeChange}
        />
      </GridWrapper>
      <NotificationPopup />
    </>
  );
};

export default LiveCourseDataTab;
